import React, { createContext, useEffect, useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import zoomSdk from "@zoom/appssdk";
import { useSearchParams } from "react-router-dom";

import { MeetingPlatform } from "@app/common/types";
import { configureZoomSdk, isZoomSupportedPlatform } from "@app/embedded/utils";
import log from "@app/utils/logger";
import { loadScript } from "@app/utils/scripts";

import { getSessionStorage } from "../storage";

export interface AppContextProps {
  client: MeetingPlatform;
}

export interface AppProviderProps {
  children: React.ReactNode;
}

export const AppContext = createContext<AppContextProps>({
  client: "unknown",
});

const GOOGLE_MEET_ADD_ON_SCRIPT_SRC =
  "https://www.gstatic.com/meetjs/addons/0.7.0/meet.addons.js";

// Zoom is our default if we are on a zoom supported platform
const getDefaultClient: () => AppContextProps["client"] = () =>
  isZoomSupportedPlatform() ? "zoom" : "browser";

const getInitClient: () => AppContextProps["client"] = () => {
  return (
    (getSessionStorage().getItem("client") as AppContextProps["client"]) ||
    getDefaultClient()
  );
};

const setInitClient = (client: AppContextProps["client"]) => {
  getSessionStorage().setItem("client", client);
};

export const AppContextProvider = ({ children }: AppProviderProps) => {
  const [queryParams] = useSearchParams();
  const clientQueryParam = queryParams.get(
    "client"
  ) as AppContextProps["client"];

  // For now we default to zoom until zoom approves our home url change
  const initClient = clientQueryParam || getInitClient();

  // The zoom app will always first load their home page which will
  // have the client as a query param. After that we want to be resilient to other
  // urls that do not have this query param, so we save the client from the initial
  // page load if we do see a different param we'll set the client to that new param
  const [client, setClient] = useState<AppContextProps["client"]>(initClient);
  const [clientSdk, setClientSdk] = useState<any>(undefined);
  const [meetSdkLoaded, setMeetSdkLoaded] = useState<boolean>(false);

  // Update client if there's a new query param
  useEffect(() => {
    if (!clientQueryParam || clientQueryParam === client) return;
    setClient(clientQueryParam);
    setInitClient(clientQueryParam);
  }, [clientQueryParam]);

  useEffect(() => {
    setInitClient(initClient);
  }, []);

  useEffect(() => {
    if (client !== "meet" || meetSdkLoaded) return;

    loadScript({
      src: GOOGLE_MEET_ADD_ON_SCRIPT_SRC,
      id: "meet-embedded-app-sdk",
    }).catch(() =>
      Sentry.captureException(new Error("Failed to load Meet SDK script"))
    );

    const meetCheckInterval = window.setInterval(() => {
      if (window.meet) {
        setMeetSdkLoaded(true);
        window.clearInterval(meetCheckInterval);
      }
    }, 100);

    return () => window.clearInterval(meetCheckInterval);
  }, [client, meetSdkLoaded]);

  useEffect(() => {
    if (client === "zoom") {
      if (isZoomSupportedPlatform()) {
        log.debug("initializing zoom sdk in AppContext");
        configureZoomSdk()
          .then(() => {
            log.debug("zoom sdk successfully initialized in AppContext");
            setClientSdk(zoomSdk);
          })
          .catch((error) =>
            log.error("failed to initialize zoom sdk AppContext", error)
          );
      } else {
        log.error("not a zoom supported platform, reverting client to browser");
        setClient("browser");
        setInitClient("browser");
      }
    }
  }, [zoomSdk, client]);

  useEffect(() => {
    log.info("client:", client);
  }, [client]);

  const appContext: AppContextProps = useMemo(
    () => ({
      client,
      clientSdk,
    }),
    [client, clientSdk]
  );

  return (
    <AppContext.Provider value={appContext}>{children}</AppContext.Provider>
  );
};
